import type { SystemType, PendulumParams } from '../types.ts';

export interface Energies { ke: number; pe: number; ee: number }

const G_RIGID = 9.81;
const G_SC = 9.80665;

export function computeEnergies(
  system: SystemType,
  params: PendulumParams,
  state: number[],
): Energies {
  if (system === 'sculpture') return computeSculptureEnergies(params, state);
  if (system === 'resonantPendulum') return computeResonantEnergies(params, state);
  return computeDoublePendulumEnergies(system, params, state);
}

function computeDoublePendulumEnergies(
  system: SystemType,
  params: PendulumParams,
  state: number[],
): Energies {
  const { m1, m2, L1, L2 } = params;
  const t1 = state[0], w1 = state[1], t2 = state[4], w2 = state[5];
  const g = G_RIGID;

  let l1: number, l2: number;
  if (system === 'rigid') {
    l1 = L1; l2 = L2;
  } else {
    l1 = L1 + state[2]; l2 = L2 + state[6];
  }

  const x1 = l1 * Math.sin(t1);
  const y1 = -l1 * Math.cos(t1);
  const y2 = y1 - l2 * Math.cos(t2);

  let sr1 = 0, sr2 = 0;
  if (system === 'elastic12' || system === 'nonlinear') {
    sr1 = state[3]; sr2 = state[7];
  }

  const vx1 = sr1 * Math.sin(t1) + l1 * w1 * Math.cos(t1);
  const vy1 = -sr1 * Math.cos(t1) + l1 * w1 * Math.sin(t1);
  const vx2 = vx1 + sr2 * Math.sin(t2) + l2 * w2 * Math.cos(t2);
  const vy2 = vy1 - sr2 * Math.cos(t2) + l2 * w2 * Math.sin(t2);

  const ke = 0.5 * m1 * (vx1 * vx1 + vy1 * vy1) + 0.5 * m2 * (vx2 * vx2 + vy2 * vy2);
  const pe = m1 * g * y1 + m2 * g * y2;

  let ee = 0;
  if (system === 'elastic12' || system === 'nonlinear') {
    const dl1 = state[2], dl2 = state[6];
    if (system === 'nonlinear') {
      const absDl1 = Math.abs(dl1), absDl2 = Math.abs(dl2);
      ee = params.k1 * L1 * (Math.exp(absDl1 / L1) - 1 - absDl1 / L1)
          + params.k2 * L2 * (Math.exp(absDl2 / L2) - 1 - absDl2 / L2);
    } else {
      ee = 0.5 * params.k1 * dl1 * dl1 + 0.5 * params.k2 * dl2 * dl2;
    }
  }

  return { ke, pe, ee };
}

function computeSculptureEnergies(
  params: PendulumParams,
  state: number[],
): Energies {
  const g = G_SC;
  const { sculptureWeight, sculptureRod, sculptureAxle, sculptureReduction, sculptureN } = params;
  const n = Math.max(1, Math.min(4, Math.floor(sculptureN)));
  const r = sculptureReduction;
  const a0 = sculptureAxle;
  const b0 = sculptureRod - a0;

  const th = [state[0], state[2], state[4], state[6]];
  const om = [state[1], state[3], state[5], state[7]];

  const masses: number[] = [];
  const aArr: number[] = [];
  const bArr: number[] = [];
  let ri = 1;
  for (let i = 0; i < n; i++) {
    masses.push(sculptureWeight * ri);
    aArr.push(a0 * ri);
    bArr.push(b0 * ri);
    ri *= r;
  }

  let ke = 0;
  let pe = 0;
  let ax = 0, ay = 0;
  let vx = 0, vy = 0;

  for (let i = 0; i < n; i++) {
    const ai = aArr[i];
    const bi = bArr[i];
    const mi = masses[i];

    const bobX = ax + ai * Math.sin(th[i]);
    const bobY = ay - ai * Math.cos(th[i]);

    const rBob = ai;
    const vbx = vx + rBob * om[i] * Math.cos(th[i]);
    const vby = vy + rBob * om[i] * Math.sin(th[i]);

    ke += 0.5 * mi * (vbx * vbx + vby * vby);
    pe += mi * g * bobY;

    vx = vbx;
    vy = vby;
    ax -= bi * Math.sin(th[i]);
    ay += bi * Math.cos(th[i]);
  }

  return { ke, pe, ee: 0 };
}

function computeResonantEnergies(
  params: PendulumParams,
  state: number[],
): Energies {
  const g = G_SC;
  const { rpM0, rpM1, rpL0, rpL1, rpA0 } = params;
  const b0 = rpL0 - rpA0;
  const a0 = rpA0;
  const a1 = rpL1;
  const t0 = state[0], w0 = state[1], t1 = state[4], w1 = state[5];

  const vx0 = a0 * w0 * Math.cos(t0);
  const vy0 = a0 * w0 * Math.sin(t0);
  const y0 = -a0 * Math.cos(t0);

  const ax = -b0 * Math.sin(t0);
  const ay = b0 * Math.cos(t0);

  const vx1 = vx0 + a1 * w1 * Math.cos(t1);
  const vy1 = vy0 + a1 * w1 * Math.sin(t1);
  const y1 = ay - a1 * Math.cos(t1);

  const ke = 0.5 * rpM0 * (vx0 * vx0 + vy0 * vy0) + 0.5 * rpM1 * (vx1 * vx1 + vy1 * vy1);
  const pe = rpM0 * g * y0 + rpM1 * g * y1;

  return { ke, pe, ee: 0 };
}

export function getAngles(system: SystemType, sculptureN: number, state: number[]): number[] {
  const n = system === 'sculpture' ? Math.max(1, Math.min(4, Math.floor(sculptureN))) : 2;
  const angles: number[] = [];
  if (system === 'sculpture') {
    for (let i = 0; i < n; i++) angles.push(state[i * 2]);
  } else {
    angles.push(state[0]);
    angles.push(state[4]);
  }
  return angles;
}

export function getVelocities(system: SystemType, sculptureN: number, state: number[]): number[] {
  const n = system === 'sculpture' ? Math.max(1, Math.min(4, Math.floor(sculptureN))) : 2;
  const vels: number[] = [];
  if (system === 'sculpture') {
    for (let i = 0; i < n; i++) vels.push(state[i * 2 + 1]);
  } else {
    vels.push(state[1]);
    vels.push(state[5]);
  }
  return vels;
}
